/* eslint-disable react-hooks/exhaustive-deps */
import React, {useContext, useEffect, useState} from "react"
import {Radio, Spin} from "antd"
import {chain, sortBy} from "lodash"
import {useTranslation} from "react-i18next"
import {AuthNetEnvironment, DispatchDataResponse, useAcceptJs} from "react-acceptjs"

import {Label} from "components/inputs/Label"
import {BaseButton, BaseInput} from "components"
import {KlassDropdown} from "components/Select"
import {handleError, handleErrorMessage} from "helpers"
import {useModel} from "hooks"
import {addressService, studentPaymentService} from "services"
import styles from "./AuthorizeNetPayment.module.css"
import {GeneralPayment} from "types/student-account/ledger-accounts"
import {StudentPaymentContext} from "context/StudentPaymentContext"
import {StudentCharge} from "types/student-account/student-ledger"
import {PaymentPlanItem} from "types/student-account/payment-plan"
import {PaymentCreatedFromPlatform} from "types/student-account/student-payment"

type Props = {
    providerConfig
    studentId
    paymentAmount
    currency
    isCheckingOut: boolean
    selectedPayment?: GeneralPayment
    customNote: string
    payerFullName?: string
    selectedCharges?: StudentCharge[]
    selectedPaymentItems?: PaymentPlanItem[]
    academicYear?: number
}

const splitAccountHolderName = (fullName: string): {firstName: string; lastName: string} => {
    const parts = fullName.trim().split(/\s+/).filter(Boolean)
    const firstName = parts[0] ?? ""
    const lastName = parts.slice(1).join(" ") || firstName
    return {firstName, lastName}
}

type PaymentMethod = "card" | "ach"

interface BasicCardInfo {
    fullName: string
    zip: string
    cardNumber: string
    cardCode: string
    month: string
    year: string
}

interface BankAccountInfo {
    accountType: "checking" | "savings"
    routingNumber: string
    accountNumber: string
    nameOnAccount: string
}

interface FormBasicCardInfo {
    firstName: string
    lastName: string
    country: any
    state: any
    zip: string
    cardNumber: string
    cardCode: string
    expiry: string
}

export const AuthorizeNetPayment = (props: Props) => {
    const {
        providerConfig,
        studentId,
        paymentAmount,
        currency,
        isCheckingOut,
        selectedPayment,
        customNote,
        payerFullName,
        selectedCharges,
        selectedPaymentItems,
        academicYear
    } = props

    const model = useModel()
    const {t} = useTranslation(["user", "common"])

    const authData = {
        apiLoginID: providerConfig?.loginId,
        clientKey: providerConfig?.clientKey
    }

    const {dispatchData, loading, error} = useAcceptJs({
        environment: (providerConfig?.paymentEnvironment || "PRODUCTION")?.toUpperCase() as AuthNetEnvironment,
        authData
    })
    const [cardData, setCardData] = useState<FormBasicCardInfo>({
        firstName: "",
        lastName: "",
        country: null,
        state: null,
        zip: "",
        cardNumber: "",
        expiry: "",
        cardCode: ""
    })
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("card")
    const [bankData, setBankData] = useState<BankAccountInfo>({
        accountType: "checking",
        routingNumber: "",
        accountNumber: "",
        nameOnAccount: ""
    })
    const [studentPayment, setStudentPayment] = useState<any | undefined>()
    const [countries, setCountries] = useState([])
    const [states, setStates] = useState([])
    const [isDropDown, setIsDropDown] = useState(false)
    const {setPaymentStatus, setPaymentError} = useContext(StudentPaymentContext)

    useEffect(() => {
        searchCountries()
    }, [])

    useEffect(() => {
        if (cardData.country?.label === "United States" || cardData.country === "United States") {
            setIsDropDown(true)
            searchStates("", cardData.country?.value)
            if (typeof cardData.state === "string") {
                setCardData((prev) => ({
                    ...prev,
                    state: ""
                }))
            }
        } else {
            if (typeof cardData.state === "object") {
                setCardData((prev) => ({
                    ...prev,
                    state: ""
                }))
            }
            setIsDropDown(false)
        }
    }, [cardData.country])

    const searchCountries = async (search = "") => {
        try {
            const params = !search ? {} : {filter: {search}}
            const response = await addressService.getCountries(params)
            const countries = response.data
                .filter((country) => !!country.name)
                .map((country) => ({value: country.countryId, label: country.name}))
            setCountries(countries)
            return countries
        } catch (e) {
            handleError(e)
        }
    }

    const searchStates = async (search = "", countryId = 0) => {
        try {
            const params = {
                filter: {
                    search,
                    countryId
                }
            }
            if (!search) {
                delete params.filter.search
            }
            const response = await addressService.getStates(params)
            const states = chain(response.data)
                .groupBy("type")
                .map((value, key) => ({
                    label: key,
                    options: sortBy(value, ["name"]).map((state) => ({value: state.stateId, label: state.name}))
                }))
                .value()
            setStates(states)
            return states
        } catch (e) {
            handleError(e)
        }
    }

    useEffect(() => {
        if (!model.user || !countries?.length) {
            return
        }

        ;(async function loadStudentData() {
            let country: any = model.user.country ?? null
            let state: any = model.user.state ?? null

            if (country) {
                country = countries.find((el) => el.label === country) ?? null
            }

            if (country?.label === "United States") {
                setIsDropDown(true)
                const statesData = await searchStates(state, country.value)
                state = statesData[0]?.options?.find((el) => el.label === state) ?? null
            } else {
                setIsDropDown(false)
            }

            setCardData((prev) => ({
                ...prev,
                firstName: model.user.firstName ?? "",
                lastName: model.user.lastName ?? "",
                country: country,
                state,
                zip: model.user.postalCode ?? ""
            }))
        })()
    }, [model.user, countries])

    useEffect(() => {
        if (!studentId || !paymentAmount || !isCheckingOut) {
            setStudentPayment(undefined)
            return
        }

        ;(async function initStudentPayment() {
            try {
                const {studentPayment} = await studentPaymentService.createAuthorizeNetStudentPayment({
                    amountInCents: Math.round(paymentAmount * 100),
                    currency,
                    studentProfileId: studentId,
                    generalPaymentId: selectedPayment?.generalPaymentId,
                    payerFullName,
                    academicYear,
                    // One-time payment only (AuthorizeNet recurring setup goes through
                    // RecurringPaymentCheckout instead) — link every selected item at its own
                    // remaining amount, not the aggregate total.
                    paymentPlanItems: selectedPaymentItems?.length
                        ? selectedPaymentItems
                              .filter((item) => item.paymentAmount)
                              .map((item) => ({
                                  paymentPlanId: item.paymentPlanId,
                                  paymentPlanItemId: item.paymentPlanItemId,
                                  amount: item.paymentAmount
                              }))
                        : undefined,
                    notes: `${selectedPayment?.name || ""}${customNote ? `: ${customNote}` : ""}`,
                    createdFromPlatform: PaymentCreatedFromPlatform.EdularWeb
                })
                if (selectedCharges?.length) {
                    await studentPaymentService.assignPaymentCharges(
                        studentPayment.paymentId,
                        selectedCharges.map((charge) => ({chargeId: charge.chargeId, amount: charge.paymentAmount}))
                    )
                }
                setStudentPayment(studentPayment)
            } catch (error) {
                handleError(error)
            }
        })()
    }, [
        studentId,
        isCheckingOut,
        paymentAmount,
        currency,
        selectedPayment,
        customNote,
        payerFullName,
        selectedCharges,
        selectedPaymentItems
    ])

    const validateBeforeSubmit = () => {
        const attrs = {
            firstName: {type: "string", errorMessage: t("common:validation.cantEmpty", {field: "First Name"})},
            lastName: {type: "string", errorMessage: t("common:validation.cantEmpty", {field: "Last Name"})},
            country: {type: "object", errorMessage: t("common:validation.hasToBeSelect", {field: "Country"})},
            zip: {type: "string", errorMessage: t("common:validation.cantEmpty", {field: "Zip"})},
            cardNumber: {type: "string", errorMessage: t("common:validation.cantEmpty", {field: "Card Number"})},
            expiry: {type: "string", errorMessage: t("common:validation.cantEmpty", {field: "Expiry"})},
            cardCode: {type: "string", errorMessage: t("common:validation.cantEmpty", {field: "Card Code"})}
        }
        for (let [key, value] of Object.entries(attrs)) {
            if (!cardData[key]) {
                handleErrorMessage(value.errorMessage)
                return true
            } else if (
                (value.type === "string" && cardData[key].trim() === "") ||
                (value.type === "array" && cardData[key].length < 1) ||
                (value.type === "object" && Object.keys(cardData[key]).length < 1)
            ) {
                handleErrorMessage(value.errorMessage)
                return true
            }
        }
        if (isDropDown) {
            if (Object.keys(cardData.state).length < 1) {
                handleErrorMessage(t("common:validation.hasToBeSelect", {field: "State"}))
                return true
            }
        } else if (cardData.state.trim() === "") {
            handleErrorMessage(t("common:validation.cantEmpty", {field: "State"}))
            return true
        }
        return false
    }

    const validateAch = (): boolean => {
        if (!bankData.nameOnAccount.trim()) {
            handleErrorMessage(t("common:validation.cantEmpty", {field: "Account Holder Name"}))
            return true
        }
        if (!bankData.routingNumber.trim()) {
            handleErrorMessage(t("common:validation.cantEmpty", {field: "Routing Number"}))
            return true
        }
        if (!bankData.accountNumber.trim()) {
            handleErrorMessage(t("common:validation.cantEmpty", {field: "Account Number"}))
            return true
        }
        if (!cardData.country || Object.keys(cardData.country).length < 1) {
            handleErrorMessage(t("common:validation.hasToBeSelect", {field: "Country"}))
            return true
        }
        if (isDropDown) {
            if (!cardData.state || Object.keys(cardData.state).length < 1) {
                handleErrorMessage(t("common:validation.hasToBeSelect", {field: "State"}))
                return true
            }
        } else if (!cardData.state || cardData.state.trim() === "") {
            handleErrorMessage(t("common:validation.cantEmpty", {field: "State"}))
            return true
        }
        if (!cardData.zip.trim()) {
            handleErrorMessage(t("common:validation.cantEmpty", {field: "Zip"}))
            return true
        }
        return false
    }

    const handleSubmit = async (event) => {
        try {
            event.preventDefault()

            setPaymentStatus("processing")
            setPaymentError(undefined)

            let response: DispatchDataResponse

            if (paymentMethod === "ach") {
                if (validateAch()) {
                    setPaymentStatus(undefined)
                    return
                }
                response = await dispatchData({
                    bankData: {
                        accountType: bankData.accountType,
                        routingNumber: bankData.routingNumber,
                        accountNumber: bankData.accountNumber,
                        nameOnAccount: bankData.nameOnAccount,
                        echeckType: "WEB"
                    } as any
                })
            } else {
                const {firstName, lastName, country, state, zip, cardNumber, cardCode, expiry} = cardData
                if (validateBeforeSubmit()) {
                    setPaymentStatus(undefined)
                    return
                }
                const fullName = `${firstName} ${lastName}`
                const expiryData = (expiry ?? "").split("/")
                const newCardData: BasicCardInfo = {
                    fullName,
                    zip,
                    cardNumber: (cardNumber ?? "").replace(/\s/g, ""),
                    cardCode,
                    month: expiryData[0] ?? "",
                    year: expiryData[1] ?? ""
                }
                response = await dispatchData({cardData: newCardData})
            }

            const {firstName, lastName, country, state, zip} = cardData
            const achPayerName = paymentMethod === "ach" ? splitAccountHolderName(bankData.nameOnAccount) : null
            const paymentFields = {
                payerFirstName: achPayerName ? achPayerName.firstName : firstName,
                payerLastName: achPayerName ? achPayerName.lastName : lastName,
                country: country?.label ?? "",
                state: isDropDown ? state?.label ?? "" : state ?? "",
                zip,
                authorizeNetPaymentNonce: response
            }

            const paymentResult = await studentPaymentService.completeAuthorizeNetStudentPayment({
                studentProfileId: studentPayment.studentProfileId,
                paymentId: studentPayment.paymentId,
                ...paymentFields
            })
            setPaymentStatus("confirmed")
            if (paymentResult) {
                window.location.href = `${window.location.origin}?paymentId=${studentPayment.paymentId}`
            }
        } catch (error: DispatchDataResponse | any) {
            if (error?.messages?.message[0]?.text) {
                setPaymentStatus("failed")
                setPaymentError(error?.messages?.message[0]?.text || "")
            } else {
                setPaymentStatus("failed")
                setPaymentError(error.message || "")
            }
        }
    }

    // Billing address is required by the completion endpoint for both card and ACH payments,
    // so it is collected once and shared between the two forms below.
    const addressFields = (
        <>
            <div className={styles.payFields}>
                <p className={styles.bodyTitle}>{t("user.country")}</p>
                <KlassDropdown
                    value={cardData.country}
                    placeholder={t("common:selectField.placeholder")}
                    onChange={(option: any) => setCardData({...cardData, country: option})}
                    valueKey="value"
                    labelKey="label"
                    options={countries}
                />
            </div>
            <div className={styles.payFields}>
                <p className={styles.bodyTitle}>{t("user.state")}</p>
                {isDropDown ? (
                    <KlassDropdown
                        value={cardData.state}
                        placeholder={t("common:selectField.placeholder")}
                        onChange={(option: any) => setCardData({...cardData, state: option})}
                        options={states}
                        valueKey="value"
                        labelKey="label"
                        isGrouped
                    />
                ) : (
                    <BaseInput
                        onChange={(value) => setCardData({...cardData, state: value})}
                        placeholder={t("user.state")}
                        value={cardData.state}
                    />
                )}
            </div>
            <div className={styles.payFields}>
                <Label text={t("common:studentInfo.postalCode")}>
                    <BaseInput onChange={(value) => setCardData({...cardData, zip: value})} value={cardData.zip} />
                </Label>
            </div>
        </>
    )

    return (
        <>
            {!authData || !studentPayment ? (
                <div>
                    <Spin />
                </div>
            ) : (
                <>
                    <div className={styles.payFields}>
                        <Radio.Group
                            value={paymentMethod}
                            onChange={(e) => setPaymentMethod(e.target.value)}
                            style={{marginBottom: 16}}>
                            <Radio value="card">Pay by Card</Radio>
                            <Radio value="ach">Pay by Bank Account (ACH)</Radio>
                        </Radio.Group>
                    </div>

                    {paymentMethod === "ach" ? (
                        <>
                            <div className={styles.payFields}>
                                <Label text="Account Holder Name">
                                    <BaseInput
                                        onChange={(value) => setBankData({...bankData, nameOnAccount: value})}
                                        value={bankData.nameOnAccount}
                                        placeholder="Full name on account"
                                    />
                                </Label>
                            </div>
                            <div className={styles.payFields}>
                                <Label text="Account Type">
                                    <Radio.Group
                                        value={bankData.accountType}
                                        onChange={(e) => setBankData({...bankData, accountType: e.target.value})}>
                                        <Radio value="checking">Checking</Radio>
                                        <Radio value="savings">Savings</Radio>
                                    </Radio.Group>
                                </Label>
                            </div>
                            <div className={styles.payFields}>
                                <Label text="Routing Number">
                                    <BaseInput
                                        onChange={(value) => setBankData({...bankData, routingNumber: value})}
                                        value={bankData.routingNumber}
                                        placeholder="9-digit routing number"
                                        maxLength={9}
                                    />
                                </Label>
                            </div>
                            <div className={styles.payFields}>
                                <Label text="Account Number">
                                    <BaseInput
                                        onChange={(value) => setBankData({...bankData, accountNumber: value})}
                                        value={bankData.accountNumber}
                                        placeholder="Bank account number"
                                    />
                                </Label>
                            </div>
                            {addressFields}
                            <div className={styles.doneBtn}>
                                <BaseButton
                                    title={"Pay"}
                                    isActive
                                    loading={loading}
                                    onClick={handleSubmit}
                                    disabled={loading || error}
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            <div className={styles.payFields}>
                                <Label text={t("common:studentInfo.firstName")}>
                                    <BaseInput
                                        onChange={(value) => setCardData({...cardData, firstName: value})}
                                        value={cardData.firstName}
                                    />
                                </Label>
                            </div>
                            <div className={styles.payFields}>
                                <Label text={t("common:studentInfo.lastName")}>
                                    <BaseInput
                                        onChange={(value) => setCardData({...cardData, lastName: value})}
                                        value={cardData.lastName}
                                    />
                                </Label>
                            </div>
                            {addressFields}
                            <div className={styles.payFields}>
                                <Label text={"Card Number"}>
                                    <BaseInput
                                        onChange={(value) => {
                                            setCardData({
                                                ...cardData,
                                                cardNumber:
                                                    (value ?? "").trim() !== ""
                                                        ? value
                                                              .replace(/\s/g, "")
                                                              .match(/.{1,4}/g)
                                                              .join(" ")
                                                        : value
                                            })
                                        }}
                                        value={cardData.cardNumber}
                                        placeholder="xxxx xxxx xxxx xxxx"
                                        maxLength={23}
                                    />
                                </Label>
                            </div>
                            <div className={styles.payFields}>
                                <Label text={"Expiry"}>
                                    <BaseInput
                                        onChange={(value) =>
                                            setCardData({
                                                ...cardData,
                                                expiry:
                                                    (value ?? "").trim().length > 2
                                                        ? value.replace(/\//g, "").replace(/^(.{2})/, "$1/")
                                                        : value
                                            })
                                        }
                                        value={cardData.expiry}
                                        placeholder="mm/yyyy"
                                        maxLength={7}
                                    />
                                </Label>
                            </div>
                            <div className={styles.payFields}>
                                <Label text={"Card Code"}>
                                    <BaseInput
                                        onChange={(value) => setCardData({...cardData, cardCode: value})}
                                        value={cardData.cardCode}
                                        placeholder="xxx"
                                        maxLength={3}
                                    />
                                </Label>
                            </div>
                            <div className={styles.doneBtn}>
                                <BaseButton
                                    title={"Pay"}
                                    isActive
                                    loading={loading}
                                    onClick={handleSubmit}
                                    disabled={loading || error}
                                />
                            </div>
                        </>
                    )}
                </>
            )}
        </>
    )
}
